import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { OUT_OF_OFFICE_PERIODS, REPLACEMENTS, USER_ID_QUERY } from '../queries';
import { SAVE_OUT_OF_OFFICE } from '../mutations';
import { OUT_OF_OFFICE_UPDATED } from '../subscriptions';
import { get, useChangeTrackingLens, over } from '@totalsoft/change-tracking-react';
import { append } from 'ramda';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { useMutation, useSubscription } from '@apollo/client';
import { useValidation } from '@totalsoft/pure-validations-react';
import { useToast } from 'utils/hooks';
import OutOfOfficeSummary from './OutOfOfficeSummary';
import { required, shape, items, concatFailure } from '@totalsoft/pure-validations';
import DoneAll from '@mui/icons-material/DoneAll';
import OutOfOfficeListComponent from './OutOfOfficeListComponent';
import { CollapseCard } from '@totalsoft/rocket-ui';
import WrappedFakeText from 'components/common/WrappedFakeText';
import moment from 'moment';
import { useSimpleError } from 'customHooks/useSimpleError';

const outOfOfficeValidator = concatFailure(
  items(
    shape({
      fromDate: required,
      toDate: required,
      replacementUserId: required
    })
  )
);
const OutOfOfficeContainer = () => {
  const { t } = useTranslation();
  const { loading, data } = useQueryWithErrorHandling(OUT_OF_OFFICE_PERIODS, { fetchPolicy: 'network-only' });
  const { loading: userInfoLoading, data: userInfoData } = useQueryWithErrorHandling(USER_ID_QUERY, {
    fetchPolicy: 'network-only'
  });
  const { data: replacementsData } = useQueryWithErrorHandling(REPLACEMENTS, { fetchPolicy: 'network-only' });
  const [outOfOfficeLens, outOfOfficeLensDirtyInfo, resetOutOfOfficeLens] = useChangeTrackingLens(null);
  const [saveOutOfOfficeDisabled, setSaveOutOfOfficeDisabled] = useState(false);
  const [addOutOfOfficeDisabled, setAddOutOfOfficeDisabled] = useState(false);
  const [outOfOfficeValidation, validateOutOfOffice] = useValidation(outOfOfficeValidator);

  const showError = useSimpleError();
  const [saveOutOfOffice] = useMutation(SAVE_OUT_OF_OFFICE, {
    onError: err => {
      showError(err);
      setSaveOutOfOfficeDisabled(false);
    }
  });
  const addToast = useToast();

  useEffect(() => {
    if (loading || !data || (outOfOfficeLens |> get)) return;
    resetOutOfOfficeLens(data.outOfOfficePeriods);
  }, [loading, data, resetOutOfOfficeLens, outOfOfficeLens]);

  const onAddNewItem = useCallback(() => {
    if (!userInfoLoading) {
      over(
        outOfOfficeLens,
        append({
          fromDate: null,
          toDate: null,
          replacementUserId: null,
          comments: null,
          replacedUserId: userInfoData?.userInfo?.userId
        })
      );
      setAddOutOfOfficeDisabled(true);
    }
  }, [outOfOfficeLens, userInfoData, userInfoLoading]);

  const handleSaveOutOfOffice = () => {
    const input = outOfOfficeLens |> get;
    if (!validateOutOfOffice(input)) {
      return;
    }
    const updatedInput = input.map(item => ({
      ...item,
      fromDate: moment(item.fromDate).format('YYYY-MM-DD'),
      toDate: moment(item.toDate).format('YYYY-MM-DD')
    }));

    setSaveOutOfOfficeDisabled(true);
    saveOutOfOffice({
      variables: {
        input: updatedInput
      }
    });
  };

  useSubscription(OUT_OF_OFFICE_UPDATED, {
    onSubscriptionData: ({
      subscriptionData: {
        data: { outOfOfficeUpdated }
      }
    }) => {
      resetOutOfOfficeLens(outOfOfficeUpdated.outOfOffices);
      setSaveOutOfOfficeDisabled(false);
      setAddOutOfOfficeDisabled(false);
      addToast(t('User.OutOfOffice.OutOfOfficeUpdated'), 'success');
    }
  });

  return (
    <CollapseCard
      icon={DoneAll}
      title={t('User.OutOfOffice.Summary.Title')}
      header={loading || !(outOfOfficeLens |> get) ? <WrappedFakeText lines={6} /> : <OutOfOfficeSummary items={outOfOfficeLens |> get} />}
      toggleOnHeaderClick
    >
      {loading ? (
        <WrappedFakeText lines={6} />
      ) : (
        <OutOfOfficeListComponent
          replacements={replacementsData?.replacements}
          onSave={handleSaveOutOfOffice}
          onAddNewItem={onAddNewItem}
          outOfOfficeLensDirtyInfo={outOfOfficeLensDirtyInfo}
          saveOutOfOfficeDisabled={saveOutOfOfficeDisabled}
          addOutOfOfficeDisabled={addOutOfOfficeDisabled}
          outOfOfficeLens={outOfOfficeLens}
          validationInfo={outOfOfficeValidation}
        />
      )}
    </CollapseCard>
  );
};

export default OutOfOfficeContainer;
